import type { FieldDefinition, Meta } from '@numistic/contracts'
import { fieldsForType, gradeRank } from '@numistic/domain'
import { useTranslation } from '@numistic/i18n'
import { Table, TBody, Td, Th, THead, Tr } from '@numistic/ui'
import { useMemo, useState, type ReactNode } from 'react'

import { displayValue } from './display.js'
import { useRefResolver } from './ref-resolver.js'

export interface MetaRow {
  id: string
  typeCode: string
  values: Record<string, unknown>
}

/** Колонки — поля с ui.showInTable у типов, присутствующих в строках (смешанные коллекции). */
export function tableColumns(
  meta: Meta,
  rows: MetaRow[],
  level: 'catalog' | 'specimen',
): FieldDefinition[] {
  const seen = new Map<string, FieldDefinition>()
  for (const typeCode of new Set(rows.map((r) => r.typeCode)))
    for (const f of fieldsForType(meta, typeCode, level))
      if (f.def.ui.showInTable) seen.set(f.def.code, f.def)
  return [...seen.values()]
}

function sortValue(meta: Meta, def: FieldDefinition, value: unknown): number | string {
  if (value === undefined || value === null) return ''
  switch (def.dataType) {
    case 'integer':
    case 'decimal':
      return Number(value)
    case 'year_range':
      return (value as { from: number }).from
    case 'grade':
      return gradeRank(meta, value as { scale: string; value: string }) ?? -1
    case 'money':
      return Number((value as { amount: string }).amount)
    default:
      return String(value).toLocaleLowerCase('ru')
  }
}

export function MetaTable({
  meta,
  rows,
  level = 'catalog',
  actions,
}: {
  meta: Meta
  rows: MetaRow[]
  level?: 'catalog' | 'specimen'
  actions?: (row: MetaRow) => ReactNode
}) {
  const { t } = useTranslation()
  const columns = useMemo(() => tableColumns(meta, rows, level), [meta, rows, level])
  const kinds = useMemo(() => new Set(columns.map((c) => c.refKind ?? '')), [columns])
  const resolve = useRefResolver(meta, kinds)
  const [sort, setSort] = useState<{ code: string; dir: 1 | -1 } | null>(null)

  const sorted = useMemo(() => {
    if (!sort) return rows
    const def = columns.find((c) => c.code === sort.code)
    if (!def) return rows
    const key = (r: MetaRow) =>
      def.dataType === 'ref'
        ? (resolve(def.refKind ?? '', String(r.values[def.code] ?? '')) ?? '')
        : sortValue(meta, def, r.values[def.code])
    return [...rows].sort((a, b) => {
      const ka = key(a)
      const kb = key(b)
      return (ka < kb ? -1 : ka > kb ? 1 : 0) * sort.dir
    })
  }, [rows, sort, columns, meta, resolve])

  if (!rows.length) return <p className="text-sm text-muted-foreground">{t('meta.noData')}</p>
  return (
    <Table>
      <THead>
        <Tr>
          {columns.map((c) => (
            <Th
              key={c.code}
              aria-sort={
                sort?.code === c.code ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined
              }
            >
              {c.ui.sortable ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 hover:text-foreground"
                  onClick={() =>
                    setSort((s) =>
                      s?.code === c.code
                        ? { code: c.code, dir: s.dir === 1 ? -1 : 1 }
                        : { code: c.code, dir: 1 },
                    )
                  }
                >
                  {c.name.ru}
                  <span aria-hidden>
                    {sort?.code === c.code ? (sort.dir === 1 ? '↑' : '↓') : ''}
                  </span>
                </button>
              ) : (
                c.name.ru
              )}
            </Th>
          ))}
          {actions ? <Th /> : null}
        </Tr>
      </THead>
      <TBody>
        {sorted.map((row) => (
          <Tr key={row.id}>
            {columns.map((c) => (
              <Td key={c.code}>{displayValue(meta, c, row.values[c.code], resolve)}</Td>
            ))}
            {actions ? <Td className="text-right">{actions(row)}</Td> : null}
          </Tr>
        ))}
      </TBody>
    </Table>
  )
}
